/**
 * The routes, and the gate in front of them.
 *
 * THE ADDRESS NAMES THE AGENT. Everything a reader looks at belongs to one agent, and its slug
 * is the first path segment: `/eco-boats`, `/eco-boats/calls/315`,
 * `/eco-boats/settings?tab=tools`. `AgentScope` finds the slug in the agent list and provides
 * it; a slug that is not in the reader's list is "No such agent", whether it does not exist or
 * belongs to somebody else. Two screens are not about one agent - `/agents/new`, where one is
 * made, and `/`, which sends the reader to the first of theirs - and the static paths rank
 * above `/:agent`, so no agent can shadow them (the Worker refuses those slugs anyway, see
 * `RESERVED_AGENT_SLUGS`).
 *
 * TWO ROUTES ARE NESTED: a call renders INSIDE the list it came from, as a dialog over it,
 * and a contact does the same over the Contacts list. See the comments on them below.
 *
 * NOTHING RENDERS UNTIL WE KNOW WHO IS ASKING. A signed-out browser gets the sign-in page
 * whatever path it asked for, and the path is left in the address bar so that arriving at
 * `/eco-boats/calls/412` from a link lands there once the reader has signed in.
 *
 * EVERY ROUTE HERE IS THE SAME FOR EVERY ROLE. What a system user gets that a member does not
 * is a wider list of agents, not a longer list of screens, and that lives in the Worker's
 * access clauses rather than here.
 */

import { Fragment, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { CallRoute } from "./pages/CallDialog";
import { Calls } from "./pages/Calls";
import { Contacts } from "./pages/Contacts";
import { ContactDialog } from "./pages/ContactDialog";
import { Overview } from "./pages/Overview";
import { Settings } from "./pages/Settings";
import { NewAgent } from "./pages/NewAgent";
import { AgentBuild } from "./pages/AgentBuild";
import { SignIn } from "./pages/SignIn";
import { Shell } from "./components/Shell";
import { Search } from "./components/Icons";
import { Empty, Skeleton, SkeletonTiles } from "./components/States";
import { useSession } from "./session";
import { AgentsProvider, CurrentAgentProvider, useAgents } from "./currentAgent";

export function App() {
  const { me, loading, error } = useSession();

  if (loading) {
    // A whole-page skeleton rather than the word "Loading". The bar and the head are
    // the same on every screen and are known before the fetch returns, so drawing them
    // immediately means the layout does not jump once it does.
    return <Booting />;
  }
  if (error) {
    // Not a 401 - the session provider treats that as "sign in". This is the API being
    // unreachable, which is worth saying plainly rather than showing a login form for.
    return (
      <div className="grid min-h-screen place-items-center p-6">
        <Empty
          tone="alert"
          title="Can't reach the dashboard"
          body={error.message}
          /* THE ONE SCREEN A MEMBER SEES WHEN WE ARE DOWN, and it used to hand them
             somebody else's bug report - "check the Worker is running on 8787". For a
             local build that line is in this comment, where the person who can act on it
             is; for an owner watching their dashboard fail, the only thing worth saying
             is that their phone is still being answered. It is also true: ingest is a
             separate path, and calls queue up and land when this comes back. */
          hint="Your agent keeps answering calls while this is down — nothing is being lost."
          action={{ label: "Try again", onClick: () => window.location.reload() }}
        />
      </div>
    );
  }
  if (!me) return <SignIn />;

  return (
    // Inside the session's gate and around every route: the switcher, the `/` redirect and
    // the agent's scope all read the one list.
    <AgentsProvider>
      <Routes>
        <Route path="/" element={<FirstAgent />} />
        {/* A spent magic link lands here after the session is established. */}
        <Route path="/signin" element={<Navigate to="/" replace />} />
        {/* Our team's: build a hosted agent from a website. The link is drawn for an internal
            reader only and the Worker answers 404 to anybody else. Not about any agent yet, so
            the shell draws no agent and no page links. */}
        <Route
          path="/agents/new"
          element={
            <Shell>
              <NewAgent />
            </Shell>
          }
        />
        <Route path="/:agent" element={<AgentScope />}>
          <Route index element={<Overview />} />
          {/* THE CALL IS A CHILD OF THE LIST, not a route beside it. `Calls` stays
              mounted while a call is open, so its filters, its loaded pages and its scroll
              position survive - and the dialog reads the rows behind it for its own
              prev/next. `/<agent>/calls/:id` is still a real URL: a link to one call lands on
              the list with that call open over it. */}
          <Route path="calls" element={<Calls />}>
            {/* The dialog in the log, and nothing in the simplified register, which draws
                the call in its own pane beside the list. See `CallRoute`. */}
            <Route path=":id" element={<CallRoute />} />
          </Route>
          {/* The people behind the calls, and one of them as a dialog over the list - the
              same nesting as a call, for the same reason. */}
          <Route path="contacts" element={<Contacts />}>
            <Route path=":id" element={<ContactDialog />} />
          </Route>
          {/* What the agent says and does. Its tabs depend on the agent: a hosted one is
              built here, a custom one only has its greeting. */}
          <Route path="settings" element={<Settings />} />
          {/* A hosted agent being built from its website, watched until it is ready. */}
          <Route path="build" element={<AgentBuild />} />
          <Route path="*" element={<NoSuchPage />} />
        </Route>
        <Route
          path="*"
          element={
            <Shell>
              <NoSuchPage />
            </Shell>
          }
        />
      </Routes>
    </AgentsProvider>
  );
}

/**
 * `/`: the first of the reader's agents, in the switcher's order.
 *
 * A REDIRECT, NOT A SCREEN OF ITS OWN, so every page anybody bookmarks or shares names its
 * agent. An empty list is the one state with nowhere to go: an organization whose only agent
 * was archived, or a database before the first is made - and for our team that is exactly
 * when "New agent" is needed.
 */
function FirstAgent() {
  const { me } = useSession();
  const { agents, loading, error, reload } = useAgents();
  if (loading) return <Booting />;
  if (error) return <AgentsUnavailable message={error.message} retry={() => void reload()} />;

  const first = agents[0];
  if (first) return <Navigate to={`/${first.slug}`} replace />;
  return (
    <Shell>
      <Empty
        title="No agent yet"
        body="There is no agent on this account yet. Once one is set up, its calls appear here."
        action={me?.user.role === "internal" ? { label: "New agent", to: "/agents/new" } : undefined}
      />
    </Shell>
  );
}

/**
 * `/:agent/*`: the agent in the address, found in the list and provided to everything below.
 *
 * A SLUG THE LIST DOES NOT HOLD IS RE-READ ONCE before it is called unknown. The list is read
 * when the app opens, so an agent made since - in another tab, or by a colleague - would
 * otherwise be "No such agent" at its own address until a reload.
 *
 * KEYED ON THE AGENT, so switching remounts the page under the bar. Nothing a page holds -
 * loaded rows, a cursor, a half-typed search, an open dialog - belongs to the next agent,
 * and a remount is the one way to be sure none of it survives the switch.
 */
function AgentScope() {
  const { agent: slug = "" } = useParams();
  const { agents, loading, error, reload } = useAgents();
  const agent = agents.find((row) => row.slug === slug);
  const [rechecked, setRechecked] = useState<string | null>(null);
  const recheck = !loading && !error && !agent && rechecked !== slug;

  useEffect(() => {
    if (!recheck) return;
    let live = true;
    void reload().finally(() => {
      if (live) setRechecked(slug);
    });
    return () => {
      live = false;
    };
  }, [recheck, reload, slug]);

  if (loading || recheck) return <Booting />;
  if (error) return <AgentsUnavailable message={error.message} retry={() => void reload()} />;
  if (!agent) {
    return (
      <Shell>
        <Empty
          icon={Search}
          title="No such agent"
          body="That address does not name an agent you can see. It may have been mistyped, or it belongs to another account."
          action={{ label: "Go to your dashboard", to: "/" }}
        />
      </Shell>
    );
  }
  return (
    <CurrentAgentProvider agent={agent}>
      <Shell>
        <Fragment key={agent.id}>
          <Outlet />
        </Fragment>
      </Shell>
    </CurrentAgentProvider>
  );
}

function AgentsUnavailable({ message, retry }: { message: string; retry: () => void }) {
  return (
    <Shell>
      <Empty tone="alert" title="Can't load your agents" body={message} action={{ label: "Try again", onClick: retry }} />
    </Shell>
  );
}

function NoSuchPage() {
  return (
    <Empty
      icon={Search}
      title="No such page"
      body="That address does not match anything in this dashboard."
      action={{ label: "Go to your dashboard", to: "/" }}
    />
  );
}

/**
 * The bar and a page-shaped hole, drawn while we find out who is asking.
 *
 * The geometry below is a deliberate copy of `Shell.tsx` - the bar's height and rule and the
 * content column have to match it exactly or the page jumps when the real shell replaces
 * this one. Change them together.
 */
function Booting() {
  return (
    <div className="flex min-h-full flex-col">
      <div aria-hidden="true" className="h-(--spacing-bar) flex-none border-b border-line bg-paper" />
      <div className="mx-auto w-full max-w-[1560px] px-8 pt-6.5 pb-16 max-[900px]:px-4 max-[900px]:pt-4.5 max-[900px]:pb-12">
        <div className="mb-6 grid gap-2.5">
          <Skeleton width={110} height={12} />
          <Skeleton width={260} height={26} />
        </div>
        <SkeletonTiles />
        <Skeleton height={260} />
        <span className="sr-only">Loading</span>
      </div>
    </div>
  );
}
